// ENG-2463: a converted purchase agreement's items table, as the PDF draws it, filled from the sale.
//
// The converter writes the table as one merge tag, {{Patient.SelectedInvoice.ItemsAsDrawn(...)}}, carrying the
// table's own headings, column widths and printed row labels. At print this fills one row per item of the sale
// in those columns. Every item prints: a hearing aid takes the first free row printed for its ear, any other
// product the first free accessory row, and an item with no printed row left gets a new row after the last one.
// A printed row nobody fills keeps its label and a line to fill in, as the original does.
//
// Column values are read through the invoice table's own getConfigurableLineItemValue with every "show" setting
// on, so a column prints what the invoice table prints for it, and the invoice table is not changed.
import type { SelectSaleConfigurationValues } from "components/word-processor-field/dialogs/word-processor-select-sale-configuration-dialog.component";
import { MERGE_TAG_REPLACED_VALUE_PREFIX } from "components/word-processor-field/merge-tags/merge-tags-constants";
import { PATIENT_SELECTED_INVOICE_OPTIONS } from "components/word-processor-field/merge-tags/merge-tags-options/patient-selected-invoice.utils";
import {
  FEE_PRODUCT_TYPE,
  HEARING_AID_PRODUCT_TYPE,
  SERVICE_PRODUCT_TYPE
} from "constants/sales";
import { getProductTypeText } from "features/inventory/products/products-list/products-list.utils";
import type { LineItem, Sale } from "services/types/sales.type";
import { formatDateOnlyValue } from "utils/common";

import { getConfigurableLineItemValue } from "./configurable-merge-tags";
import { excludeReturnedOrExchanged, getSortedItems } from "./utils";

/** What a column of the drawn table prints. `product_type` is the row's printed label; `blank` a line to fill in. */
export const ITEMS_COLUMNS = [
  "product_type",
  "ear",
  "product",
  "description",
  "manufacturer",
  "model",
  // ENG-2477: the product's Catalog name, as the practice named it in Inventory › Catalog.
  "name",
  "color",
  "battery",
  "serial",
  "warranty",
  "price",
  "qty",
  "blank"
] as const;
export type ItemsColumn = (typeof ITEMS_COLUMNS)[number];

/** The invoice table's column each key reads (getConfigurableLineItemValue's header names). */
const INVOICE_HEADER: Partial<Record<ItemsColumn, string>> = {
  ear: "Ear",
  product: "Product",
  description: "Description",
  manufacturer: "Manufacturer",
  model: "Model",
  name: "Name",
  color: "Color",
  battery: "Battery",
  serial: "Serial #",
  warranty: "MFR Warranty Exp",
  price: "Price",
  qty: "Qty"
};

export type ItemsAsDrawnOptions = {
  headers: string[];
  columns: ItemsColumn[];
  /** Each column's share of the table's width, in %. */
  widths: number[];
  /** The rows the PDF prints under the headings, by their labels ("Left Hearing Aid", "Accessory"). */
  rows: string[];
  /** The label of a row the PDF prints for services ("Additional Services"), when it has one. */
  services?: string;
  /** How many columns that row's label spans; the services listed span the rest. */
  servicesSpan: number;
  lines: string;
  sizePt: number;
  headerBold: boolean;
  labelBold: boolean;
  /** The form's width between its margins, in inches: a line to fill in is sized to its cell. */
  contentIn: number;
};

// --------------------------------------------------------------------------- the tag's options

// parseMergeTagOptions splits the options on "," and "=" and starts them at the last "(": every value is
// URI-encoded, parentheses too (encodeURIComponent leaves them), so a heading like "Make, model (L=left)" cannot
// break it. A list's items are joined by "|", which encodeURIComponent escapes inside an item.
const encode = (value: string | number) =>
  encodeURIComponent(String(value)).replace(/[()]/g, (character) =>
    character === "(" ? "%28" : "%29"
  );
const list = (values: (string | number)[]) => values.map(encode).join("|");
const unlist = (value: unknown) =>
  typeof value === "string" && value
    ? value.split("|").map((item) => decodeURIComponent(item))
    : [];

export const encodeItemsAsDrawnOptions = (options: ItemsAsDrawnOptions) =>
  [
    `headers=${list(options.headers)}`,
    `columns=${list(options.columns)}`,
    `widths=${list(options.widths)}`,
    `rows=${list(options.rows)}`,
    ...(options.services ? [`services=${encode(options.services)}`] : []),
    `services_span=${options.servicesSpan}`,
    `lines=${encode(options.lines)}`,
    `size=${options.sizePt}`,
    `header_bold=${options.headerBold ? "yes" : "no"}`,
    `label_bold=${options.labelBold ? "yes" : "no"}`,
    `content_in=${options.contentIn}`
  ].join(",");

/** The options as parseMergeTagOptions hands them over, or null when they do not describe a table. */
export const decodeItemsAsDrawnOptions = (
  options: Record<string, unknown> | undefined
): ItemsAsDrawnOptions | null => {
  if (!options) return null;
  const headers = unlist(options.headers);
  const columns = unlist(options.columns).filter(
    (column): column is ItemsColumn =>
      (ITEMS_COLUMNS as readonly string[]).includes(column)
  );
  const widths = unlist(options.widths).map(Number);
  if (
    !headers.length ||
    columns.length !== headers.length ||
    widths.length !== headers.length ||
    widths.some((width) => !Number.isFinite(width))
  )
    return null;
  const sizePt = Number(options.size);
  const contentIn = Number(options.content_in);
  return {
    headers,
    columns,
    widths,
    rows: unlist(options.rows),
    ...(typeof options.services === "string" && options.services
      ? { services: decodeURIComponent(options.services) }
      : {}),
    servicesSpan: Math.max(1, Number(options.services_span) || 1),
    lines:
      typeof options.lines === "string"
        ? decodeURIComponent(options.lines)
        : "all",
    sizePt: Number.isFinite(sizePt) && sizePt > 0 ? sizePt : 10.5,
    headerBold: options.header_bold === true,
    labelBold: options.label_bold === true,
    contentIn: Number.isFinite(contentIn) && contentIn > 0 ? contentIn : 7.5
  };
};

// --------------------------------------------------------------------------- rows

type RowKind = "left" | "right" | "aid" | "accessory" | "any";
type Row = { label: string; kind: RowKind; item?: LineItem };

const kindOfLabel = (label: string): RowKind => {
  if (/\bleft\b/i.test(label)) return "left";
  if (/\bright\b/i.test(label)) return "right";
  if (/hearing|\baid\b|device|instrument/i.test(label)) return "aid";
  if (/accessor/i.test(label)) return "accessory";
  return "any";
};

const typeOf = (item: LineItem) =>
  item.product?.master_product?.type || item.product?.type;

const isService = (item: LineItem) => {
  const type = typeOf(item);
  return type === SERVICE_PRODUCT_TYPE || type === FEE_PRODUCT_TYPE;
};

// Every column the invoice table can print, shown: getConfigurableLineItemValue reads `<kind>_show_<column>`.
const EVERY_COLUMN_SHOWN = new Proxy(
  {},
  {
    get: (_target, key) => typeof key === "string" && key.includes("_show_")
  }
) as Partial<SelectSaleConfigurationValues>;

const valueOf = (item: LineItem, column: string) =>
  getConfigurableLineItemValue(item, column, EVERY_COLUMN_SHOWN) ?? "";

/**
 * A column's value for an item. The warranty date prints when the invoice table would print it, but as
 * numbers: a purchase agreement's Warranty column is narrow (HAA's is 8.5% of the page), and "Oct 14, 2027"
 * wraps there, growing every hearing aid's row until the form's last line goes onto another page.
 */
const cellValueOf = (item: LineItem, column: ItemsColumn) => {
  const header = INVOICE_HEADER[column];
  const value = header ? valueOf(item, header) : "";
  const warrantyDate =
    item.inventory_product?.manufacturer_warranty_expiration_date;
  return column === "warranty" && value && warrantyDate
    ? formatDateOnlyValue(warrantyDate, "MM/dd/yyyy")
    : value;
};

/** Where an item may go, most fitting first. */
const kindsFor = (item: LineItem): RowKind[] => {
  if (typeOf(item) !== HEARING_AID_PRODUCT_TYPE) return ["accessory", "any"];
  const ear = valueOf(item, "Ear");
  if (/^left$/i.test(ear)) return ["left", "aid", "any"];
  if (/^right$/i.test(ear)) return ["right", "aid", "any"];
  return ["aid", "left", "right", "any"];
};

/** The printed rows, each with the item it holds, and a new row for each item no printed row could take. */
const placeItems = (printed: string[], items: LineItem[]): Row[] => {
  const rows: Row[] = printed.map((label) => ({
    label,
    kind: kindOfLabel(label)
  }));
  const extra: Row[] = [];
  items.forEach((item) => {
    const kinds = kindsFor(item);
    const free = kinds
      .map((kind) => rows.find((row) => !row.item && row.kind === kind))
      .find(Boolean);
    if (free) {
      free.item = item;
      return;
    }
    // A label the form already prints for this kind of item, so the new row reads like the others.
    const like = kinds
      .map((kind) => rows.find((row) => row.kind === kind))
      .find(Boolean);
    extra.push({
      label:
        like?.label ??
        (typeOf(item) === HEARING_AID_PRODUCT_TYPE
          ? "Hearing Aid"
          : getProductTypeText(item.product) || "Accessory"),
      kind: "any",
      item
    });
  });
  return [...rows, ...extra];
};

// --------------------------------------------------------------------------- HTML

const escapeHtml = (value: string) =>
  value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");

const UNDERSCORE_EM = 0.556;
const CELL_PADDING_IN = 8 / 96;

/** A line to fill in, as wide as the cell allows: the print dialog makes it a field (SN-19949). */
const fillLine = (widthPct: number, options: ItemsAsDrawnOptions) => {
  const cellIn = (options.contentIn * widthPct) / 100 - CELL_PADDING_IN;
  const count =
    Math.floor((cellIn * 72) / (UNDERSCORE_EM * options.sizePt)) - 1;
  return "_".repeat(Math.min(120, Math.max(3, count)));
};

const text = (value: string, options: ItemsAsDrawnOptions, bold = false) =>
  `<span style="font-size: ${options.sizePt}pt${bold ? "; font-weight: bold" : ""}">${value}</span>`;

const cell = (content: string, widthPct: number, span = 1) =>
  `<td${span > 1 ? ` colspan="${span}"` : ""} style="width: ${Math.round(widthPct * 100) / 100}%"><div>${content}</div></td>`;

// The column that names the item. The invoice table's column rules print nothing for some products (a warranty
// or repair sold as an accessory), and a row holding one must still say what it is.
const NAME_COLUMNS: ItemsColumn[] = ["name", "model", "description", "product"];
const nameColumnOf = (columns: ItemsColumn[]) =>
  NAME_COLUMNS.find((column) => columns.includes(column)) ??
  columns.find((column) => column !== "product_type" && column !== "blank");

/**
 * A line to fill in, named for the print dialog's "Fill in the blanks" panel by the column it is under and the
 * row it is on ("Serial #", "Left Hearing Aid"); unnamed, the panel lists each one as "Blank 12".
 */
const blankLine = (
  widthPct: number,
  options: ItemsAsDrawnOptions,
  label: string,
  context = ""
) =>
  `<span style="font-size: ${options.sizePt}pt" data-blank-label="${escapeHtml(label)}"${
    context ? ` data-blank-context="${escapeHtml(context)}"` : ""
  }>${fillLine(widthPct, options)}</span>`;

const itemCell = (
  row: Row,
  column: ItemsColumn,
  header: string,
  widthPct: number,
  options: ItemsAsDrawnOptions
) => {
  if (column === "product_type")
    return cell(
      text(escapeHtml(row.label), options, options.labelBold),
      widthPct
    );
  const value =
    (row.item ? cellValueOf(row.item, column) : "") ||
    (row.item && column === nameColumnOf(options.columns)
      ? row.item.description || row.item.product?.name || ""
      : "");
  if (!value)
    return cell(blankLine(widthPct, options, header, row.label), widthPct);
  // The price column is the invoice table's own HTML (a discount struck through); the rest is text.
  return cell(
    text(column === "price" ? value : escapeHtml(value), options),
    widthPct
  );
};

/** The table, or null without a sale so the Select Invoice button prints in its place. */
export const itemsAsDrawnTableHtml = (
  sale: Sale | undefined | null,
  options: ItemsAsDrawnOptions
): string | null => {
  if (!sale) return null;
  const items = getSortedItems(excludeReturnedOrExchanged(sale.items || []));
  const services = options.services ? items.filter(isService) : [];
  const products = items.filter((item) => !services.includes(item));
  const rows = placeItems(options.rows, products);

  const heading = `<tr>${options.headers
    .map((header, index) =>
      cell(
        text(escapeHtml(header), options, options.headerBold),
        options.widths[index]
      )
    )
    .join("")}</tr>`;
  const body = rows
    .map(
      (row) =>
        `<tr>${options.columns
          .map((column, index) =>
            itemCell(
              row,
              column,
              options.headers[index],
              options.widths[index],
              options
            )
          )
          .join("")}</tr>`
    )
    .join("");

  let servicesRow = "";
  if (options.services) {
    const span = Math.min(options.servicesSpan, options.columns.length);
    const labelWidth = options.widths
      .slice(0, span)
      .reduce((sum, width) => sum + width, 0);
    const listed = services
      .map((item) => item.description || item.product?.name || "")
      .filter(Boolean)
      .map(escapeHtml)
      .join("; ");
    const label = text(
      escapeHtml(options.services),
      options,
      options.labelBold
    );
    if (span >= options.columns.length) {
      servicesRow = `<tr>${cell(
        `${label} ${listed ? text(listed, options) : blankLine(100 - labelWidth / 2, options, options.services)}`,
        100,
        options.columns.length
      )}</tr>`;
    } else {
      servicesRow = `<tr>${cell(label, labelWidth, span)}${cell(
        listed
          ? text(listed, options)
          : blankLine(100 - labelWidth, options, options.services),
        100 - labelWidth,
        options.columns.length - span
      )}</tr>`;
    }
  }

  return `<table data-lines="${escapeHtml(options.lines)}" style="width: 100%; table-layout: fixed; border-collapse: collapse"><tbody>${heading}${body}${servicesRow}</tbody></table>`;
};

// --------------------------------------------------------------------------- before a sale fills it

// The tag as the converter writes it: its options are URI-encoded, so they hold no ")" of their own.
const ITEMS_AS_DRAWN_TAG = new RegExp(
  `(<p>)?\\{\\{${PATIENT_SELECTED_INVOICE_OPTIONS.ITEMS_AS_DRAWN.replaceAll(".", "\\.")}\\(([^)]*)\\)\\}\\}(</p>)?`,
  "g"
);

// As parseMergeTagOptions reads them (merge-tags.utils, which this file cannot import without a cycle).
const parseOptions = (optionsString: string) =>
  Object.fromEntries(
    optionsString.split(",").map((pair) => {
      const [key, value] = pair.split("=");
      return [key, value === "yes" ? true : value === "no" ? false : value];
    })
  );

/**
 * The form with each items table drawn as the original prints it, its rows' lines to fill in, in place of the
 * tag's text: what the form's PDF shows, and the height its pages are counted at, before a sale fills it.
 */
export const itemsTablesAsPrinted = (html: string) =>
  html.replace(
    ITEMS_AS_DRAWN_TAG,
    (match, open: string | undefined, optionsString: string, close) => {
      const layout = decodeItemsAsDrawnOptions(parseOptions(optionsString));
      if (!layout) return match;
      const table = itemsAsDrawnTableHtml({ items: [] } as never, layout);
      if (table === null) return match;
      // As the print fills it, so the page is counted at the height it prints: the tag's paragraph stays, and the
      // value goes in after the marker every filled tag gets, which prints as a line of its own before the table.
      return `${open ?? ""}${MERGE_TAG_REPLACED_VALUE_PREFIX}${table}${close ?? ""}`;
    }
  );
